import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useFilters } from '@elfsight/widget-filters';
import { useSettings } from '@wu/widget-core';
import type {
  FilterKey,
  FiltersScheme,
  FiltersStyle,
  StoreLocatorLocation
} from '../types';
import { checkOpenStatus } from './utils/check-open-status';
import { useWidgetContext } from './widget-context';

const SIDEBAR_WIDTH = 400;
const MAP_WIDTH_THRESHOLD = 480;

type LocationsFilterContext = {
  currentLocations: StoreLocatorLocation[];
  appliedCurrentLocationsCount: number;
  filteredLocationsCount: number;
  isActiveFilters: boolean;
  currentFiltersStyle: FiltersStyle;
};

const LocationsFilterContext = createContext<LocationsFilterContext | null>(
  null
);

const checkActiveFilter = (selected: string[] = [], values: string[] = []) =>
  selected.some((v) => values.includes(v));

const getCurrentFilters = (filtersObj: FiltersScheme) =>
  Object.entries(filtersObj).filter(([_, values]) => values?.length);

export function LocationsFilterProvider({ children }: PropsWithChildren) {
  const {
    locations,
    demoLocations,
    filtersStyle,
    filters: filtersSettings
  } = useSettings();

  const { widgetWidth } = useWidgetContext();
  const { filters, appliedFilters, resetFilters } = useFilters<FiltersScheme>();
  const [currentFiltersStyle, setCurrentFiltersStyle] = useState(filtersStyle);

  const effectiveLocations = locations.some(({ place }) => !!place)
    ? locations
    : demoLocations;

  useEffect(() => {
    resetFilters();
  }, [filtersSettings, resetFilters]);

  useEffect(() => {
    if (widgetWidth - SIDEBAR_WIDTH < MAP_WIDTH_THRESHOLD) {
      setCurrentFiltersStyle('sidePanel');
      return;
    }

    setCurrentFiltersStyle(filtersStyle);
  }, [widgetWidth, filtersStyle]);

  const filter = useCallback(
    (
      locationsList: StoreLocatorLocation[],
      filtersList: [FilterKey, string[]][]
    ) => {
      if (!locationsList?.length) return [];

      return locationsList.filter((location: StoreLocatorLocation) => {
        const passesFilters = filtersList.every(([key, values]) => {
          if (!values?.length) {
            return true;
          }

          switch (key) {
            case 'openNow':
              return checkOpenStatus(location);
            case 'tags':
            case 'categories':
              return checkActiveFilter(values, location[key]);
            default:
              return false;
          }
        });

        return passesFilters;
      });
    },
    []
  );

  const value = useMemo(() => {
    const currentFilters = getCurrentFilters(appliedFilters);
    const processedFilters = getCurrentFilters(filters);

    const currentLocations = filter(effectiveLocations, currentFilters);
    const processedLocations = filter(effectiveLocations, processedFilters);

    return {
      currentLocations,
      appliedCurrentLocationsCount: currentLocations.length,
      filteredLocationsCount: processedLocations.length,
      isActiveFilters: currentFilters.length > 0,
      currentFiltersStyle
    };
  }, [
    effectiveLocations,
    appliedFilters,
    filters,
    filter,
    currentFiltersStyle
  ]);

  return (
    <LocationsFilterContext.Provider value={value}>
      {children}
    </LocationsFilterContext.Provider>
  );
}

export function useLocationsFilter() {
  const context = useContext(LocationsFilterContext);
  if (!context) {
    throw new Error(
      'useLocationsFilter must be used within LocationsFilterProvider'
    );
  }

  return context;
}
